/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// reactstrap components

// This is where we will display the conversation details
// Convo Name and AI Stuff, record button, etc

import {
  Card,
  CardHeader,
  Container,
  Row,
  Button,
  Label,
  UncontrolledTooltip as Tooltip,
} from 'reactstrap';
import { Tabs, Tab } from 'react-bootstrap';
import { GlobalContext } from '../api/GlobalContext';
import { useContext, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import AudioRecorder from '../api/Recorder.js';
import { sendNativeMessage, isNativeRecorder } from '../api/nativeRecorder';
import PatientModal from 'components/Modals/PatientModal';
import InstructionAssistantModal from 'components/InstructionAssistant/InstructionAssistantModal';
import InstructionUpdateStatusStrip from 'components/InstructionAssistant/InstructionUpdateStatusStrip';
import InstructionUpdateUndoModal from 'components/InstructionAssistant/InstructionUpdateUndoModal';
import { useInstructionAssistantUpdate } from 'components/InstructionAssistant/useInstructionAssistantUpdate';
import { logGAEvent } from '../api/googleAnalytics';
// core components
import Header from 'components/Headers/Header';
import HashState from '../variables/hash';
import getFullName from '../components/Misc/getFullName';
import EditableLabel from '../components/Misc/EditableLabel';
import Collapsible, {
  WriteNotesCollapsible,
} from '../components/Misc/Collapsible';
import ConfirmationModal from '../components/Misc/ConfirmationModal.js';
import DictationButton from '../components/Misc/DictationButton';
import OutlinedSelector from '../components/Misc/OutlinedSelector';
import ComplianceScore from '../components/Misc/ComplianceScore';
import {
  createComprehendTool,
  createChangePatientTool,
} from '../components/Misc/Synapse/ConversationDetailsTools.js';
import { FAKE_TRANSCRIPT } from '../components/Misc/Synapse/Prompts.js';
import { isDemoMode } from '../utils/demoStorage';
import {
  learnResultBanner,
  learnTemplateSections,
} from '../utils/learnTemplate';
import { saveLearnExample } from '../utils/learnExample';

import {
  extractExistingValuesOnly,
  handleGetComplianceScore as handleGetComplianceScoreUtil,
  getScoreRefreshLabel,
  getScoreSurfaceLabel,
} from '../components/Misc/complianceUtils.js';
import { OptionsDropdown } from '../components/Misc/ConversationDetailsUtils.js';

import {
  onAddSample,
  onEditSample,
  onRemoveSample,
} from '../components/Misc/samplesUtil.js';
import {
  fetchSharedTemplateDetails,
  fetchUserConfigs,
} from '../components/Misc/apiUtils';
import LoadingBanner from '../components/Misc/LoadingBanner.js';
import MicDrop from '../api/MicDrop';
import { externalConnectionAvailable } from '../EMR_Integration/react_webInjector.ts';
import Chat from '../components/Misc/Chat.js';
import Coding from '../components/Misc/Coding.js';
import NavigationPath from '../components/Misc/NavigationPath.js';
import Modal from 'components/Modals/Modal';
import vendorStore, { patientFromRecord, isVendorAppsEnabled } from '../utils/vendorApps/store';
import VendorSubscriptionsSection from '../components/VendorApps/VendorSubscriptionsSection';
import useVisitWorkspace from '../components/Workspace/useVisitWorkspace';
import { renderWorkspaceTabs } from '../components/Workspace/WorkspaceTabs';
import { complianceCompactTitle } from '../components/Workspace/ItemGlyph';
import WorkspaceIcon from '../components/Workspace/WorkspaceIcon';
import AppData from '../components/VendorApps/AppData';
import { truncateText } from '../components/Misc/utils';
import { GRADE_TEXT_COLORS, gradeForScore } from '../components/Misc/complianceGrades';

const ConversationDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('response'); // Track active tab
  const [tabsVisible, setTabsVisible] = useState(false); // Control tab visibility

  // Default Values for the fields
  const defaultName = 'Conversation Name';
  const defaultStatus = 'Loading...';
  const defaultConfig = 'Default';
  const defaultGroup = 'None';
  const defaultGroupId = -1;
  const defaultTranscript = '';
  const defaultVisitNotes = '';
  const defaultSecondsTranscribed = 0;
  const defaultQuestions = {};
  const defaultConfigId = -1;
  const defaultTemplate = {};
  const defaultSamples = {};
  const defaultEMR = '';

  // Get our Globals
  const { apiClient } = useContext(GlobalContext);

  // State Variables
  const [conversationID, setConversationID] = useState(-1); // This is the ID of the conversation we are looking at
  const [refresh, _setRefresh] = useState(false); // This is used to force a refresh of the conversation fields (like when we change the config)
  const [name, _setName] = useState(defaultName);
  const [status, _setStatus] = useState(defaultStatus);
  const [config, _setConfig] = useState(defaultConfig);
  const [group, __setGroup] = useState(defaultGroup);
  const [groupId, _setGroupId] = useState(defaultGroupId);
  const [transcript, _setTranscript] = useState(defaultTranscript);
  const [visitNotes, _setVisitNotes] = useState(defaultVisitNotes);
  const [appContext, _setAppContext] = useState({}); // connected apps' context for this visit, keyed by app id (its own field)
  const [secondsTranscribed, _setSecondsTranscribed] = useState(
    defaultSecondsTranscribed,
  );
  const [emr, setEmr] = useState(defaultEMR);
  const [questions, _setQuestions] = useState(defaultQuestions);
  const [configId, _setConfigId] = useState(defaultConfigId);
  const [templateQuestions, _setTemplateQuestions] = useState(defaultTemplate);
  const [templateSamples, _setTemplateSamples] = useState(defaultSamples);
  const [viewMode, setViewMode] = useState('');
  const hash = HashState();
  const hashRef = useRef(hash);
  hashRef.current = hash;
  const [questionBatching, setQuestionBatching] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [isDowngraded, setIsDowngraded] = useState(false);
  const [rateLimit, setRateLimit] = useState(null);
  const [confirmationDetails, setConfirmationDetails] = useState({
    isOpen: false,
    type: '', // 'sample' or 'template'
    questionKey: '',
    index: -1,
  });
  const [aiThinking, setAiThinking] = useState(false);
  const [instructionAssistantOpen, setInstructionAssistantOpen] =
    useState(false);
  const getInstructionAssistantSectionMetadata = () =>
    Object.keys(templateQuestions || {}).map((key) => ({
      key,
      currentAnswer: questions[key] ?? '',
    }));
  const instructionAssistantUpdate = useInstructionAssistantUpdate({
    apiClient,
    configId,
    conversationId:
      conversationID && conversationID !== -1 ? conversationID : null,
    instructions: {
      getCurrent: () => templateQuestions,
      applyLocal: (revisedInstructions) =>
        _setTemplateQuestions((currentTemplateQuestions) => ({
          ...currentTemplateQuestions,
          ...revisedInstructions,
        })),
      getSectionMetadata: getInstructionAssistantSectionMetadata,
    },
    examples: {
      getCurrent: () => templateSamples,
      applyLocal: (revisedExamples) =>
        _setTemplateSamples((currentSamples) => ({
          ...currentSamples,
          ...revisedExamples,
        })),
    },
    note: {
      snapshot: async (changedKeys) =>
        (changedKeys || []).map((key) => ({
          Key: key,
          Question: key,
          Answer: questions[key] ?? '',
        })),
      reComprehend: async (changedKeys) => {
        if ((changedKeys || []).length > 0) {
          await comprehend(changedKeys, null, (key, status) => {
            document.dispatchEvent(
              new CustomEvent('updateComprehendStatus', {
                detail: { key, status },
              }),
            );
          });
        }
      },
      revert: (answers) => {
        const restoredQuestions = { ...questions };
        (answers || []).forEach(({ Question, Answer }) => {
          restoredQuestions[Question] = Answer;
        });
        setQuestions(restoredQuestions);
      },
    },
    onError: (message) => toast.error(message),
  });
  const resetInstructionAssistantUpdate = instructionAssistantUpdate.reset;

  useLayoutEffect(() => {
    resetInstructionAssistantUpdate();
    setInstructionAssistantOpen(false);
  }, [location.hash, resetInstructionAssistantUpdate]);

  const [bannerText, setBannerText] = useState('Comprehending...');
  const [triggerShow, setTriggerShow] = useState(false);
  // State for compliance score and feedback
  const [complianceScore, _setComplianceScore] = useState(0); // Default to 0
  const [complianceFeedback, _setComplianceFeedback] = useState([]); // Default to empty array
  const [complianceCriteriaSource, _setComplianceCriteriaSource] =
    useState('cms'); // Default to CMS
  const [complianceScoreLoading, setComplianceScoreLoading] = useState(false);
  // Scorecard flag: "Scorecard" copy when on, "Compliance" copy when off.
  const scoreSurfaceLabel = getScoreSurfaceLabel(apiClient);
  const scoreRefreshLabel = getScoreRefreshLabel(apiClient);
  const [userEmail, setUserEmail] = useState(null);
  const [showEditorFields, setShowEditorFields] = useState(false);
  const [showWriteNotes, setShowWriteNotes] = useState(
    () => localStorage.getItem('writeNotesOpen') === 'true',
  );
  const toggleWriteNotes = () => {
    const next = !showWriteNotes;
    setShowWriteNotes(next);
    localStorage.setItem('writeNotesOpen', next);
  };
  const inputRef = useRef();
  const [codingRecommendations, setCodingRecommendations] = useState([]);
  const [codingPayerName, setCodingPayerName] = useState(null);
  const [codingAutoRun, setCodingAutoRun] = useState(null);

  const setRefresh = () => _setRefresh(!refresh);

  // Functions to update the state variables (and send to the server)
  const setName = (newName) => {
    _setName(newName);
    apiClient.editConversation(conversationID, 'name', newName);
  };

  /**
   * Tool Magic
   */
  // Create a single ref to hold all action handlers for external access (tools, etc)
  const actionsRef = useRef({});

  useEffect(() => {
    // Initialize the OpenAIAssistant
    const comprehendTool = createComprehendTool(async () => {
      // Only set fake transcript if current transcript has no real content (just date/patient info)
      const currentTranscript = actionsRef.current.transcript || '';
      const wordCount = currentTranscript
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length;
      const lineCount = currentTranscript.trim().split('\n').length;
      const hasOnlyBasicInfo =
        isDemoMode() &&
        wordCount <= 25 &&
        lineCount <= 2 &&
        (currentTranscript.includes('Date of visit:') ||
          currentTranscript.includes('Patient:') ||
          currentTranscript.trim() === '');
      if (hasOnlyBasicInfo) {
        await actionsRef.current.setTranscript(FAKE_TRANSCRIPT);
      }
      // await setTranscript(apiClient.transcript); (Should totally append and also consider a cleaning mechanism)
      actionsRef.current.comprehend();
    });
    apiClient.addTools([comprehendTool]);
    return () => {
      // Unregister the tool magic with the API client
      apiClient.removeTools([comprehendTool.name]);
    };
  }, []);

  // Mount transcript as dynamic context - updates whenever transcript changes
  useEffect(() => {
    if (!transcript || transcript.trim() === '') return; // Don't add empty transcript

    apiClient.addContext('current_transcript', {
      transcript: transcript,
      wordCount: transcript
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length,
      conversationId: conversationID,
    });

    return () => {
      apiClient.removeContext('current_transcript');
    };
  }, [transcript, conversationID]);

  // Mount patient prior visit details as dynamic context - updates when patient changes
  useEffect(() => {
    if (!groupId || groupId === -1) return;
    apiClient.getPatientHistory(groupId, 3).then((data) => {
      if (!data || data.error) return;
      apiClient.addContext('current_patient_prior_visit_details', {
        ...data,
        conversationId: conversationID,
      });
    });
    return () => apiClient.removeContext('current_patient_prior_visit_details');
  }, [groupId, conversationID]);

  const setConfig = (newConfig) => {
    // Check if the config is new and if so create a modal asking the user to create a new config
    if (newConfig.access) {
      setViewMode(newConfig.access);
    }
    if (newConfig.name === 'New') {
      apiClient
        .newTemplate()
        .then((data) => {
          const { configId, name } = data;
          // Update state after new template is created
          navigate(`/User/templates/manager#${configId}`);
          setUniqueConfigs([
            ...new Set([...uniqueConfigs, { name, configId }]),
          ]);
          _setConfig(name);
          _setConfigId(configId);
        })
        .catch((error) => {
          // probably no session key
          // console.log(error);
          console.log('Did not create new template');
        });
      return;
    }
    // Add the new entry to the list of unique groups
    //setUniqueConfigs([...new Set([...uniqueConfigs, {name:newConfigName, configId:newConfigId}])]);
    _setConfig(newConfig.name);
    _setConfigId(newConfig.configId);
    // When we edit the config and there is not an existing config the server duplicates the existing config
    apiClient
      .editConversation(
        conversationID,
        'configId',
        parseInt(newConfig.configId),
      )
      .catch((data) => {
        // console.log(data)
        alert('Failed to update config, Try again.');
      })
      .finally(() => {
        // Update the questions
        setRefresh();
      });
  };

  const _setGroup = (newGroup) => {
    /*
            Description:
                This function is called when the user selects a new group from the dropdown
            Args:
                newGroup: The name of the new group

            Notes:
                We need to lookup the group id based on the newGroup name and set the groupId to the uid
            */

    // Check if the group is new and if so create a modal asking the user to create a new group
    if (newGroup === 'New') {
      setPatientModelOpen(true);
      return;
    }

    // Lookup the group id
    // If we found it, set the ID and Name
    var patientID = apiClient.patients.filter(
      (patient) => getFullName(patient) === newGroup,
    )[0];
    if (patientID === undefined) {
      // console.log(`Could not find patient with name ${newGroup}`);
      // Not ready yet, and that's okay
      return;
    }
    patientID = patientID.uid;

    __setGroup(newGroup);
    _setGroupId(patientID);
    return patientID;
  };

  const setGroup = (newGroup) => {
    if (newGroup == 'None') {
      var patientID = -1;
    } else {
      var patientID = _setGroup(newGroup);
    }
    // console.log(`patientID = ${patientID}`)
    if (patientID || newGroup == 'None') {
      // We should send this change to the server
      apiClient
        .editConversation(conversationID, 'group', patientID)
        .then((data) => {
          if ('error' in data) {
            console.log('Error editing conversation');
            console.log(data);
            return;
          }
        })
        .catch((data) => {
          // Update the questions
          setRefresh();
          alert('Failed to update Patient, Try again.');
        });

      // Add the new entry to the list of unique groups...
      // setUniqueGroups([...new Set([...uniqueGroups, patientInfo])]);
      // Update the user's patient list from the server after successfully making the new patient
    }
  };
  // Vendor Apps: with the feature off (and no Dev Console override) none of the
  // effects below run — the store is never told about this visit and nothing is
  // written to app_context. Recomputed each render so a flag/dev-mode flip on a
  // profile refresh re-runs them.
  const vendorAppsOn = isVendorAppsEnabled(apiClient);

  // What this visit can open, in the clinician's order: pinned items are tabs,
  // the rest live in the Apps tab (same layout as the extension's tabs + rail).
  const workspace = useVisitWorkspace({
    builtins: [
      'note',
      'chat',
      apiClient.showComplianceScore && 'compliance',
      apiClient.features?.show_coding_tab && 'coding',
    ].filter(Boolean),
    vendorAppsOn,
    activeTab,
    setActiveTab,
    labels: apiClient.features?.scorecard ? { compliance: 'Scorecard' } : {},
  });
  // Vendor Apps (docs/vendor-apps-contract.md §4–5): this view IS the open
  // chart on the web app (the CX view is the same thing inside the extension).
  // Tell the store which patient/visit is open so vendor frames get
  // `comprehend:patient`, setContext is checked against it, and the note gets
  // the right buffers. Cleared on unmount so nothing is "open" between visits.
  useEffect(() => {
    if (!vendorAppsOn) return undefined;
    // GUARANTEE: once this visit has a patient, the store (and every app frame)
    // gets one. On a fresh load straight into a visit, apiClient.patients can
    // arrive after this effect runs, so retry briefly; if the list still lacks
    // the record, fall back to what the visit itself knows (id + display name)
    // rather than telling apps "no chart open".
    let timer = null;
    let tries = 0;
    const attempt = () => {
      if (!groupId || groupId === -1) {
        vendorStore.setPatient(null, conversationID);
        return;
      }
      const record = apiClient.patients?.find((p) => String(p.uid) === String(groupId));
      if (record) {
        vendorStore.setPatient(patientFromRecord(record), conversationID);
        return;
      }
      if (tries++ < 20) {
        timer = setTimeout(attempt, 500);
        return;
      }
      vendorStore.setPatient({ id: String(groupId), name: group || '', dob: null }, conversationID);
    };
    attempt();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorAppsOn, apiClient, groupId, conversationID, apiClient?.patients?.length]);
  useEffect(() => () => vendorStore.setPatient(null, null), []);
  // The clinician clicked "Use <app>'s patient" on a visit with no patient: find
  // that person in the clinic's list by name + DOB, create them if absent, put
  // them on this visit. The patient effect above then runs and the store re-runs
  // whatever the app sent through the normal guard.
  useEffect(() => {
    if (!vendorAppsOn) return undefined;
    const assign = async ({ appId, name, dob }) => {
      try {
        const parts = String(name || '').trim().split(/\s*,\s*|\s+/).filter(Boolean);
        const [first, last] = name.includes(',') ? [parts[1] || '', parts[0] || ''] : [parts[0] || '', parts.slice(1).join(' ')];
        const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
        const sameDob = (a, b) => !a || !b || String(a).slice(0, 10) === String(b).slice(0, 10);
        let record = (apiClient.patients || []).find((r) => same(r.firstName, first) && same(r.lastName, last) && sameDob(r.dob, dob));
        if (!record) {
          const made = await apiClient.newPatient(first, last, dob || null);
          if (made?.error) throw new Error(made.error);
          const fresh = await apiClient.getUserGroups();
          if (fresh?.groups) apiClient.patients = fresh.groups;
          record = (apiClient.patients || []).find((r) => String(r.uid) === String(made.uid)) || { uid: made.uid, firstName: first, lastName: last, dob };
        }
        const rsp = await apiClient.editConversation(conversationID, 'group', record.uid);
        if (rsp?.error) throw new Error(rsp.error);
        __setGroup(getFullName(record));
        _setGroupId(record.uid);
        toast.success(`${getFullName(record)} is now on this visit (from ${vendorStore.getApp(appId)?.name || 'the app'}).`);
      } catch (e) {
        toast.error(`Couldn't pull that patient in: ${e.message}`);
      }
    };
    return vendorStore.on('assignPatient', assign);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorAppsOn, conversationID]);
  // New transcript/note content invalidates cached subscription answers.
  useEffect(() => {
    if (vendorAppsOn && transcript) vendorStore.bumpContext();
  }, [vendorAppsOn, transcript]);

  const setTranscript = (newTranscript) => {
    _setTranscript(newTranscript);
    apiClient.editConversation(conversationID, 'transcript', newTranscript);
  };
  const setVisitNotes = (newVisitNotes) => {
    _setVisitNotes(newVisitNotes);
    apiClient.editConversation(conversationID, 'visit_notes', newVisitNotes);
  };
  // Dictation appends to whatever is already in Clinical Facts so several
  // dictations (and anything typed in between) stack up instead of overwriting
  // each other. Read through a ref because the dictation callback is created
  // once and would otherwise close over a stale value.
  const visitNotesRef = useRef(visitNotes);
  visitNotesRef.current = visitNotes;
  // Vendor Apps (docs/vendor-apps-contract.md §5): what connected apps set as
  // context for the open patient is saved on the visit as its own field,
  // app_context, keyed by app id; the backend folds it into every prompt after
  // the clinician's own notes. Updated whenever an app provides or clears.
  // Working copy that is updated synchronously: several apps provide within the
  // same tick when a visit opens, and React state only catches up on re-render.
  // Without this, each event merged into a stale map and the saves overwrote
  // each other — you got one app's data or the other.
  const appContextRef = useRef(appContext);
  useEffect(() => {
    appContextRef.current = appContext;
  }, [appContext]);
  // Saves are coalesced: a short grace period lets every app land, then ONE
  // write carries the whole map. Flushed on leave so nothing is lost.
  // The pending save carries the map it will write and the visit it belongs to,
  // so a flush always writes that pair — never the current ref against an older
  // visit id (which could put visit B's app data on visit A's record).
  const appContextSaveRef = useRef({ timer: null, convo: null, value: null });
  const flushAppContext = () => {
    const pending = appContextSaveRef.current;
    if (!pending.timer) return;
    clearTimeout(pending.timer);
    pending.timer = null;
    apiClient.editConversation(pending.convo, 'app_context', pending.value || {});
  };
  const setAppContext = (next) => {
    const pending = appContextSaveRef.current;
    if (pending.timer && pending.convo !== conversationID) flushAppContext(); // switching visits: save the old one, with its own map, now
    appContextRef.current = next;
    _setAppContext(next);
    clearTimeout(pending.timer);
    pending.convo = conversationID;
    pending.value = next;
    pending.timer = setTimeout(() => {
      pending.timer = null;
      apiClient.editConversation(pending.convo, 'app_context', pending.value || {});
    }, 700);
  };
  useEffect(() => {
    if (!vendorAppsOn) return undefined;
    const sync = ({ appId, patientId }) => {
      if (!conversationID || !vendorStore.patient || patientId !== vendorStore.patient.id) return;
      const live = vendorStore.buffersForOpenPatient().find((b) => b.appId === appId);
      const next = { ...(appContextRef.current || {}) };
      if (live) next[appId] = { ...(next[appId] || {}), name: live.app, markdown: live.markdown, providedAt: live.providedAt, edited: false };
      else delete next[appId];
      if (JSON.stringify(next) !== JSON.stringify(appContextRef.current || {})) setAppContext(next);
    };
    // Answers we computed for an app are kept too — by structure hash, latest only —
    // so the note-tab rows show them after a reload and there is a record of what
    // each app was told for this visit.
    const syncAnswers = ({ appId, hash, payload }) => {
      if (!conversationID || !vendorStore.patient || !hash || payload?.patientId !== vendorStore.patient.id) return;
      const cur = appContextRef.current || {};
      const entry = cur[appId] || { name: vendorStore.getApp(appId)?.name || 'app', markdown: '', providedAt: null, edited: false };
      const answers = { ...(entry.answers || {}), [hash]: { answers: payload.answers, answeredAt: payload.answeredAt, version: payload.contextVersion } };
      setAppContext({ ...cur, [appId]: { ...entry, answers } });
    };
    const off = vendorStore.on('buffer', sync);
    const offAnswers = vendorStore.on('answers', syncAnswers);
    return () => {
      off();
      offAnswers();
      flushAppContext();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorAppsOn, conversationID]);
  const appendVisitNotes = (text) => {
    const addition = (text || '').trim();
    if (!addition) {
      return;
    }
    const existing = (visitNotesRef.current || '').replace(/\s+$/, '');
    setVisitNotes(existing ? `${existing}\n${addition}` : addition);
  };
  const setQuestions = (newQuestions) => {
    // Do a delta between the old and new questions as the API wants just the changed question
    for (const key in newQuestions) {
      if (questions[key] !== newQuestions[key]) {
        // If the values are different, send the change
        apiClient
          .editConversation(conversationID, key, newQuestions[key])
          .then((data) => {
            if ('error' in data) {
              console.log('Error editing conversation');
              return;
            }
          })
          .catch((error) => {
            console.log(`Unexpected Error from convo edit - ${error}`);
          });
      }
    }
    _setQuestions(newQuestions);
  };

  useEffect(() => {
    // Reset the state variables when the hash changes...
    _setName(defaultName);
    _setStatus(defaultStatus);
    _setConfig(defaultConfig);
    _setGroup(defaultGroup);
    _setSecondsTranscribed(defaultSecondsTranscribed);
    _setTranscript(defaultTranscript);
    _setQuestions(defaultQuestions);
    _setConfigId(defaultConfigId);
    _setTemplateQuestions(defaultTemplate);
    _setTemplateSamples(defaultSamples);
    setCodingRecommendations([]);
    setCodingPayerName(null);
    setCodingAutoRun(null);

    if (hash === '') {
      console.log('No conversation id specified');
      navigate('/');
      return;
    }
    setConversationID(parseInt(hash, 10));
    setEmr(defaultEMR);
  }, [hash]);

  // Prime the native recorder with the open visit so a recording started from
  // the Apple Watch has context before the phone's Record button is pressed.
  // Scoped to this view because it is the only place exactly one visit is open.
  useEffect(() => {
    let isNativeHost = false;
    try {
      isNativeHost = !!window.ReactNativeWebView || isNativeRecorder();
    } catch (_) {}
    if (!isNativeHost) return undefined;
    if (!(conversationID > 0)) return undefined;

    if (apiClient?.sessionKey) sendNativeMessage('sessionKey', apiClient.sessionKey);
    if (apiClient?.sessionEmail) sendNativeMessage('sessionEmail', apiClient.sessionEmail);
    sendNativeMessage('convoID', conversationID);
    sendNativeMessage('convoIDs', [conversationID]);

    return () => {
      // Leaving the visit clears the target, so the Watch can't record into it.
      sendNativeMessage('convoID', null);
      sendNativeMessage('convoIDs', []);
    };
  }, [apiClient, conversationID]);

  useEffect(() => {
    // When template selection changes, update the questions and samples
    fetchConfigDetails(configId);
  }, [apiClient, configId, refresh]);

  useEffect(() => {
    // console.log("Refreshing Conversation Details")
    // Go get the conversation details for this conversation
    // const hash = window.location.hash.substr(1);
    // Get the conversation details
    updateConversationDetails();
  }, [apiClient, conversationID, refresh]);

  useEffect(() => {
    // When status value is set to "Refresh to see the results!", start a timer to refresh the details every 15 seconds until
    // status is set to "AI Complete!" or up to 2 minutes and then stop the timer
    if (status === 'Refresh to see the results!') {
      var count = 0;
      var interval = setInterval(() => {
        if (status !== 'AI Complete!' && count < 8) {
          setRefresh(); // Call setRefresh every 15 seconds
          count++;
        } else {
          clearInterval(interval); // Stop the timer if status is "AI Complete!" or count reaches 8
        }
      }, 15000);

      return () => {
        clearInterval(interval); // Cleanup: stop the interval when the component unmounts or when status changes
      };
    }
    return () => {}; // Do nothing when status is not "Refresh to see the results!"
  }, [status]); // Run the effect whenever the status changes

  useEffect(() => {
    if (showEditorFields === 'visit' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showEditorFields]);

  async function fetchConfigDetails(configId) {
    apiClient
      .getConfigDetails(configId)
      .then(async (data) => {
        if ('error' in data) {
          return;
        }
        _setConfig(data.name);
        _setConfigId(configId);
        _setTemplateQuestions(data.questions || defaultQuestions);
        _setTemplateSamples(data.samples || defaultSamples);
        let sharedDetails = await fetchSharedTemplateDetails(
          apiClient,
          configId,
        );

        if (sharedDetails) {
          setViewMode(sharedDetails.access_level);
        } else {
          setViewMode('Full');
        }
      })

      .catch((error) => {
        console.log(error);
      });
  }

  function updateConversationDetails() {
    let localConvoID = conversationID;
    if (localConvoID == -1) {
      localConvoID = parseInt(hashRef.current, 10);
    }

    apiClient
      .getConversationDetails(localConvoID)
      .then((data) => {
        console.log(data);
        if ('error' in data) {
          console.log('Error getting conversation details');
          _setStatus(data.error);
          return;
        }

        // Verify we are looking at the right conversation and something has not changed since we started
        // Protection against a race condition and asynchronous calls...
        var realHash = hashRef.current;
        if (localConvoID == parseInt(realHash, 10)) {
          // only set the values if the field is not null or undefined
          _setName(data.name || defaultName);
          _setStatus(data.status || defaultStatus);
          _setConfig(data.config || defaultConfig);
          setEmr(data.emr || defaultEMR);

          // Set Compliance score
          _setComplianceScore(data.score || 0); // Default to 0 if undefined
          // Prefer requirement_evaluations if available, otherwise fall back to feedback
          const feedbackToUse =
            data.requirement_evaluations || data.feedback || [];
          _setComplianceFeedback(feedbackToUse);

          // group is the uid of the patient
          // _setGroup(data.group || defaultGroup);
          _setGroupId(data.group || defaultGroupId);
          _setTranscript(data.transcript || defaultTranscript);
          _setVisitNotes(data.visit_notes || defaultVisitNotes);
          // Connected apps' context for this visit (its own field, keyed by app id).
          _setAppContext(data.app_context || {});
          _setSecondsTranscribed(data.seconds || defaultSecondsTranscribed);
          _setQuestions(data.questions || defaultQuestions);
          setCodingRecommendations(data.coding_recommendations || []);
          setCodingPayerName(data.coding_recommendations_payer_name || null);

          // First set configId, then fetch config details
          const newConfigId = data.configId || defaultConfigId;
          _setConfigId(newConfigId); // Update the configId state

          // Now fetch the new config details based on the newly set configId
          fetchConfigDetails(newConfigId);
          // if this comes second, then do the filter, else just set the default
          var patientName = apiClient.patients.filter(
            (patient) => patient.uid === data.group,
          )[0];
          if (patientName) {
            _setGroup(getFullName(patientName));
          } else {
            _setGroup(defaultGroup);
            setTriggerPatients(triggerPatients + 1);
          }
        }
      })
      .catch((error) => {
        // If we get a string, then we can use it directly, otherwise we need to get the message from the error object
        const errorMsg =
          typeof error === 'string'
            ? error
            : (error && error.message) || String(error);
        // If error starts with Expected: then we can ignore it
        if (errorMsg.startsWith('Expected:')) {
          console.log('Error getting conversation details');
          console.log(errorMsg);
          return;
        }
        // probably no session key or no ConvoID Yet
        console.log(errorMsg);
        console.log('Did not get conversations details');
      });
  }

  const updateQuestion = (key, newAnswer) => {
    const newQuestions = { ...questions };
    newQuestions[key] = newAnswer;
    setQuestions(newQuestions);
  };

  const updateInstructions = (key, newInstructions) => {
    const newTemplateQuestions = { ...templateQuestions };
    newTemplateQuestions[key] = newInstructions;
    _setTemplateQuestions(newTemplateQuestions);
    apiClient
      .editConfig(configId, 'update_question_value', key, newInstructions)
      .then((data) => {
        if ('error' in data) {
          console.log('Error editing config');
          return;
        }
      });
  };

  const openConfirmationModal = (type, questionKey, index) => {
    setConfirmationDetails({ isOpen: true, type, questionKey, index });
  };

  const handleConfirmAction = () => {
    if (confirmationDetails.type === 'sample') {
      onRemoveSample(
        confirmationDetails.questionKey,
        confirmationDetails.index,
        configId,
        _setTemplateSamples,
        apiClient,
      );
    }
    setConfirmationDetails({ ...confirmationDetails, isOpen: false });
  };

  // So want something to highlight when we click into the field
  // We also need to capture these edits/changes and send them to the server when complete

  // Get all user Groups
  const defaultGroups = ['None', 'New'];
  const [uniqueGroups, setUniqueGroups] = useState(defaultGroups);

  // Get all user Configs
  const [uniqueConfigs, setUniqueConfigs] = useState([
    { name: 'New', configId: 1 },
  ]);

  // If we have to make an API Call to get the configs and groups, we can do that here
  const [triggerPatients, setTriggerPatients] = useState(0);
  useEffect(() => {
    const fetchPatients = () => {
      return apiClient
        .updatePatients()
        .then((patientsData) => {
          // set the unique groups to be the list of patients and defaultGroups
          // just grab the fullNames from the paitents, not ethe actual objects
          var patientNames = apiClient.patients.map((patient) =>
            getFullName(patient),
          );
          setUniqueGroups([...new Set([...uniqueGroups, ...patientNames])]);

          // set the group given the patientID
          var patientName = apiClient.patients.filter(
            (patient) => patient.uid === groupId,
          )[0];

          if (patientName) {
            _setGroup(getFullName(patientName));
          }
        })
        .catch((error) => {
          console.error('Error updating patients:', error);
          return [];
        });
    };

    const fetchUserSharedConfigs = () => {
      return apiClient
        .getStats({})
        .then((statsData) => {
          if (!statsData || !statsData.email) {
            return Promise.reject(
              'Error getting user stats — session may have expired',
            );
          }
          const email = statsData.email;
          const should_batch = statsData.isBatchingEnabled;
          setUserEmail(email);
          const domain = email.split('@')[1];
          setQuestionBatching(should_batch);
          return apiClient.getUserSharedConfigs('@' + domain);
        })
        .then((sharedConfigsData) => {
          if (!sharedConfigsData || !sharedConfigsData.sharedConfigs) {
            return Promise.reject('Error getting user shared configs');
          }
          return sharedConfigsData.sharedConfigs.map((config) => ({
            name: config.sharedConfigName,
            configId: config.sharedConfigId,
            access: config.access_level,
          }));
        })
        .catch((error) => {
          console.error('[fetchUserSharedConfigs]', error);
          return [];
        });
    };

    Promise.all([
      fetchPatients(),
      fetchUserConfigs(apiClient),
      fetchUserSharedConfigs(),
    ])
      .then(([patientsData, userConfigsData, sharedConfigsData]) => {
        if (userConfigsData && userConfigsData.length > 0) {
          userConfigsData.map((config) => ({
            name: config.name,
            configId: config.configId,
            access: 'Full',
          }));
        }
        const allConfigs = [
          ...uniqueConfigs,
          ...userConfigsData,
          ...sharedConfigsData,
        ];
        const uniqueConfigsNews = [
          ...new Set(allConfigs.map(JSON.stringify)),
        ].map(JSON.parse);
        setUniqueConfigs(uniqueConfigsNews);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  }, [apiClient, setUniqueGroups, setUniqueConfigs, triggerPatients]);

  function showRateLimitModal() {
    // Display the modal to the user
    setPopupVisible(true);
  }

  function handleRateLimitConfirmation() {
    setPopupVisible(false); // Hide modal
    setIsDowngraded(true); // Set downgraded mode
    console.log('Retrying comprehension with downgraded mode:', isDowngraded);
  }

  // useEffect to detect changes in `isDowngraded` and retry comprehension
  useEffect(() => {
    if (isDowngraded) {
      console.log('Retrying comprehension with downgraded mode:', isDowngraded);
      comprehend(); // Retry comprehension with updated state
    }
  }, [isDowngraded]); // Run when `isDowngraded` changes

  // Call the API to comprehend the conversation
  async function comprehend(
    questionKey,
    depth,
    updateSectionStatusIndicator = null,
  ) {
    logGAEvent('comprehend_conversation', apiClient, {
      conversation_id: conversationID,
    });

    if (!apiClient.actionAllowed()) {
      _setStatus('No Credits Remaining');
      return { status: 'error' };
    }
    // If questionKey is null, then we want to comprehend all questions
    // const questionKeys = questionKey instanceof String ? [questionKey] : Object.keys(questions);
    // Check if we are editing the conversation box and if so wait for the edit to finish
    // console.log("Checking if we are editing")
    // console.log(depth)
    if (depth == undefined && window.onbeforeunload) {
      // delay .5 seconds and see if it goes away
      setTimeout(() => {
        if (window.onbeforeunload) {
          console.log('Window was dead, trying again');
          comprehend(questionKey, true);
        }
      }, 500);

      return { status: 'pending' };
    }
    // _setStatus("Please wait for the edit to finish before comprehending");

    const questionKeys = Array.isArray(questionKey)
      ? questionKey
      : typeof questionKey === 'string'
        ? [questionKey]
        : Object.keys(questions);

    _setStatus('Comprehending...');
    setBannerText('Comprehending...');
    setAiThinking(true);

    let errorOccurred = false; // Flag to track if an error occurred
    let hasNone = false; // Flag to track if any response is null/undefined

    var promises = [];
    function handleError(error, context) {
      console.error(`Unexpected Error in ${context} - ${error}`);
      if (apiClient.isTimeoutError(error)) {
        _setStatus('Refresh to see the results!');
      } else {
        _setStatus(error.toString());
      }
    }

    function chunkArray(array, size) {
      const result = [];
      for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
      }
      return result;
    }

    async function processQuestions(batch, answerType) {
      try {
        // Batch is [{"Test": "ABD"}, {"Test2": "BCD"}] -> Turn that into {"Test": "ABD", "Test2": "BCD"}
        const questions = batch.reduce((acc, obj) => {
          const key = Object.keys(obj)[0];
          const value = obj[key];
          acc[key] = value;
          return acc;
        }, {});
        const responses = await apiClient.getConversationQuestions(
          conversationID,
          questions,
          answerType,
        );
        if ('error' in responses) {
          _setStatus(responses.error);
          errorOccurred = true;
          return;
        } else {
          Object.entries(responses['answers']).forEach(([question, answer]) => {
            if (answer === null || answer === undefined) {
              hasNone = true;
            }
            _setQuestions((questions) => {
              const newQuestions = { ...questions };
              newQuestions[question] = answer;
              return newQuestions;
            });
          });
        }
      } catch (error) {
        handleError(error, 'batchConversation');
        errorOccurred = true;
      }
    }
    // if (questionBatching) { // Reenable this code when we add batching support for LA as currently is not supported
    //   // By default all WebApp questions are counted as Long Answers and are batched
    //   const longAnswerQuestions = [];
    //   questionKeys.forEach((qkey) => {
    //     // Append question to longAnswerQuestions
    //     longAnswerQuestions.push({ [qkey]: templateQuestions[qkey] });
    //   });
    //   // Now batch questions
    //   const longAnswerBatches = chunkArray(longAnswerQuestions, 5);
    //   // Process long answer questions
    //   promises.push(
    //     ...longAnswerBatches.map((batch) =>
    //       processQuestions(batch, 'Long Answer'),
    //     ),
    //   );
    // } else {
    for (const key of questionKeys) {
      if (updateSectionStatusIndicator)
        updateSectionStatusIndicator(key, 'pending');
      // Using a promise will go comprehend all the questions at the same time
      const promise = apiClient
        .comprehendConversation(conversationID, key, true, isDowngraded)
        .then((data) => {
          if ('error' in data) {
            // This is where empty questors are coming back and displaying weird error
            // "TypeError: Cannot read properties of undefined (reading 'includes')""
            // It should be handled more gracefully, the error is something like {'error': 'Question not found'}
            if (
              data.error.message &&
              data.error.message.includes('rate limited')
            ) {
              setRateLimit(data.error.refresh_limit);
              showRateLimitModal(); // Custom function to trigger the modal
              return; // Exit early if rate limited
            } else {
              _setStatus(data.error);
              errorOccurred = true;
            }
            if (updateSectionStatusIndicator)
              updateSectionStatusIndicator(key, 'error');
            return;
          }

          if (
            !data.answer ||
            typeof data.answer !== 'string' ||
            data.answer.toLowerCase().includes('none') ||
            (typeof data.answer === 'string' &&
              data.answer ===
                'Your input is too brief to generate an accurate note. Please provide more details, aiming for at least 25 words. The more detailed your description, the better the notes you will receive.')
          ) {
            hasNone = true;
            if (updateSectionStatusIndicator)
              updateSectionStatusIndicator(key, 'none');
          } else {
            if (updateSectionStatusIndicator)
              updateSectionStatusIndicator(key, 'success');
          }

          // Thread Safe version
          _setQuestions((questions) => {
            const newQuestions = { ...questions };
            newQuestions[key] = data.answer;
            return newQuestions;
          });
        })
        .catch((error) => {
          handleError(error, 'comprehendConversation');
          errorOccurred = true;
          if (updateSectionStatusIndicator)
            updateSectionStatusIndicator(key, 'error');
        });
      promises.push(promise);
    }
    // }
    return Promise.all(promises)
      .then(() => {
        setAiThinking(false);
        let statusResult;
        if (errorOccurred) {
          statusResult = 'error';
        } else if (hasNone) {
          statusResult = 'none';
        } else {
          statusResult = 'success';
        }
        if (!errorOccurred) {
          // Step 1: Comprehension Complete
          _setStatus('✅ Comprehension Complete!');
          setCodingAutoRun({ conversationID });
          setTriggerShow(true);
          if (apiClient.showComplianceScore) {
            setComplianceScoreLoading(true);
            _setComplianceScore(0); // Clear stale score so old grade doesn't persist during fetch

            (async () => {
              try {
                const existingValuesMapping =
                  await getExistingValuesFromWebapp();

                let response;
                try {
                  response = await Promise.race([
                    apiClient.scoreConversation(
                      conversationID,
                      existingValuesMapping,
                    ),
                    new Promise((_, reject) =>
                      setTimeout(
                        () => reject(new Error('SCORE_TIMEOUT')),
                        28000,
                      ),
                    ),
                  ]);
                } catch (raceErr) {
                  if (raceErr.message === 'SCORE_TIMEOUT') {
                    // Score endpoint timed out — poll conversation details for the saved score
                    for (let i = 0; i < 24; i++) {
                      await new Promise((r) => setTimeout(r, 5000));
                      const data =
                        await apiClient.getConversationDetails(conversationID);
                      if (data?.score) {
                        _setComplianceScore(data.score);
                        const feedbackToUse =
                          data.requirement_evaluations || data.feedback || [];
                        _setComplianceFeedback(feedbackToUse);
                        _setComplianceCriteriaSource(
                          data.criteria_source || 'cms',
                        );
                        _setStatus('🚀 Comprehension and Compliance Complete!');
                        setTriggerShow(true);
                        return;
                      }
                    }
                    _setStatus(
                      '❗Compliance unavailable. See "How to Improve?" for details',
                    );
                    setTriggerShow(true);
                    return;
                  }
                  throw raceErr;
                }

                if (response?.error) {
                  throw new Error(
                    response.error.message ||
                      response.error ||
                      'Compliance scoring failed',
                  );
                }
                if (!response?.answer) {
                  throw new Error(
                    'Compliance scoring failed: no response payload',
                  );
                }
                const {
                  score,
                  feedback,
                  requirement_evaluations,
                  criteria_source,
                } = response.answer;
                _setComplianceScore(score);
                // Prefer requirement_evaluations if available, otherwise fall back to feedback
                const feedbackToUse = requirement_evaluations || feedback || [];
                _setComplianceFeedback(feedbackToUse);
                _setComplianceCriteriaSource(criteria_source || 'cms');
                _setStatus('🚀 Comprehension and Compliance Complete!');
                setTriggerShow(true);
              } catch (scoringError) {
                _setStatus(
                  '❗Compliance unavailable. See "How to Improve?" for details',
                );
                const errorMessage =
                  scoringError?.response?.data?.error?.message ||
                  scoringError?.response?.data?.error ||
                  scoringError?.message ||
                  scoringError?.toString?.() ||
                  'Compliance scoring failed';
                toast.error(errorMessage, { pauseOnFocusLoss: false });
                console.log(`Error: ${scoringError}`);
                setTriggerShow(true);
              } finally {
                setComplianceScoreLoading(false);
              }
            })();
            // Check for EMR Type and call patchRaintreeEmr if emr == "Raintree"
            console.log('EMR:', emr);
            if (emr === 'Raintree') {
              patchEMR(conversationID);
            }
          } else {
            // Compliance score off: comprehension succeeded, keep the ✅ status.
            setAiThinking(false);
            setTriggerShow(true);
          }
        }
        return { status: statusResult, hasNone };
      })
      .catch((comprehensionError) => {
        // Handle General Errors in Comprehension Process
        setAiThinking(false);
        _setStatus('Error in Comprehension');
        // Handle errors if any of the promises fail
        console.log(`Error: ${comprehensionError}`);
      });
  }

  function patchEMR(conversationID) {
    setAiThinking(true);
    setBannerText('Publishing to EMR...');
    apiClient
      .patchRaintreeEMR(conversationID)
      .then((response) => {
        if (response.error) {
          // Handle specific error responses from the API
          console.error('Error updating EMR:', response.error);
          toast.error(
            `Failed to update EMR: ${response.error || response.error.details}`,
            {
              pauseOnFocusLoss: false, // (Cross your fingers...)
            },
          );
        } else if (response.success) {
          // Extract the missing counts from the response
          const missingFromMapping = response.missing_keys_from_mapping || [];
          const missingFromRaintree = response.missing_keys_from_raintree || [];
          const totalMissing =
            missingFromMapping.length + missingFromRaintree.length;

          let message = `UPDATE: ${response.updated_fields.length} fields updated successfully.`;
          if (totalMissing > 0) {
            message += ` Unable to update ${totalMissing} value(s) in your Raintree Record.`;
          }
          toast.success(message);
        } else {
          // Handle generic successful response without specific success message
          toast.info(
            'EMR update processed. Check details for specific outcomes.',
          );
        }

        // Additional data from the response can be handled here
        if (response.missing_keys_from_mapping?.length > 0) {
          console.warn(
            'Missing keys from mapping:',
            response.missing_keys_from_mapping,
          );
        }
        if (response.missing_keys_from_raintree?.length > 0) {
          console.warn(
            'Missing Raintree keys:',
            response.missing_keys_from_raintree,
          );
        }
      })
      .catch((error) => {
        toast.error(`Failed to patch EMR: ${error.message || error}`);
      })
      .then(() => {
        // Technically, this should only run if we opened it... but the state of the var isn't
        // updated yet so we don't know....
        setAiThinking(false);
      });
  }

  const [isPatientModalOpen, setPatientModelOpen] = useState(false);

  function newConfig() {
    apiClient
      .newTemplate()
      .then((data) => {
        // console.log(data);
        navigate(`/User/templates/manager#${data['configId']}`);
      })
      .catch((error) => {
        // probably no session key
        // console.log(error);
        console.log('Did not create new template');
      });
  }

  const generateNoteText = (includeAllDetails = false) => {
    let noteText = '';
    if (includeAllDetails) {
      noteText += `Conversation Name: ${name}\n`;
      noteText += `Patient: ${group}\n`;
      noteText += `Template: ${config}\n\n`;
    }
    for (const key in questions) {
      const question = questions[key];
      if (!question.startsWith(key)) {
        noteText += `${key}:\n`;
      }
      noteText += `${question}\n\n\n`;
    }
    return noteText;
  };

  const copyTextToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text to clipboard:', err);
    }
  };

  const copyAll = async () => {
    const noteText = generateNoteText(true);
    await copyTextToClipboard(noteText);
  };

  const copyResponse = async () => {
    const noteText = generateNoteText();
    await copyTextToClipboard(noteText);
  };

  function newPatient(first, last, dob) {
    // Create the New Patient
    // add the patient to the list of patients
    var formattedName = getFullName({ firstName: first, lastName: last });
    uniqueGroups.push(formattedName);
    __setGroup(formattedName);

    apiClient.newPatient(first, last, dob).then((data) => {
      // Get the UID for the new patient
      var patientId = data['uid'];
      // Assign the patient to the conversation
      apiClient.updatePatients().then((data) => {
        var patient = apiClient.patients.filter(
          (patient) => patient.uid === patientId,
        )[0];
        setGroup(getFullName(patient));
        setTriggerPatients(triggerPatients + 1);
      });
    });
  }

  let value = { name: config, configId: configId };
  const disableEditing = viewMode === 'View Only' || viewMode === 'Duplicate';
  const showInstructionAssistant = !disableEditing && viewMode !== 'AutoCopy';
  // Push-to-talk into Clinical Facts. Flagged off by default; the field stays
  // typeable either way, so a disabled flag only hides the button.
  const showDictation = !!apiClient.features?.dictation;
  const showDictationButton = showDictation && showWriteNotes;
  // Record + Dictation + Comprehend rarely fit one line on a narrow window, so
  // drop Comprehend to its own full-width row underneath — the same shape the
  // CX panel always uses. Only when Dictation is actually on screen; without it
  // the original two-button row still fits.
  const isNarrowScreen = useMediaQuery({ query: '(max-width: 991px)' });
  const stackComprehend = showDictationButton && isNarrowScreen;


  // First-run tutorial popup (the hosted slide deck) no longer auto-opens: the
  // onboarding checklist is the guide now. It stays reachable from the sidebar's
  // "Getting started".

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Handler to update online/offline internet status
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /* Conditional showing of the comprehend button */
  const [disableComprehend, setDisableComprehend] = useState(false);
  useEffect(() => {
    if (status === 'Start Recording!') {
      setDisableComprehend(true);
      setTabsVisible(false); // Hide tabs when recording hasn't started
      console.log('****');
    } else if (status === 'Uploading Audio...') {
      setBannerText('Uploading Audio...');
      setAiThinking(true);
      setDisableComprehend(true);
    } else {
      // Only hide banner if we're not in another AI thinking state and the banner is for uploading
      if (
        bannerText === 'Uploading Audio...' &&
        status !== 'Comprehending...' &&
        !status.includes('Compliance')
      ) {
        setAiThinking(false);
      }
      setDisableComprehend(false);
      setTabsVisible(true); // Show tabs when not in "Start Recording!" status
      // No tab switch here: a status change leaves the clinician on whatever
      // tab they're on. The visit opens on their first tab once, below.
    }
  }, [status, bannerText]);

  // The visit opens on the clinician's first tab — Note unless they moved
  // something ahead of it.
  // Once per visit: another visit opened in place lands on the first tab too.
  const homeTabAppliedRef = useRef(false);
  useEffect(() => {
    homeTabAppliedRef.current = false;
  }, [conversationID]);
  useEffect(() => {
    if (homeTabAppliedRef.current || !workspace.loaded || status === defaultStatus) return;
    homeTabAppliedRef.current = true;
    workspace.open(workspace.pinned[0]?.id || 'note');
  }, [status, workspace.loaded, conversationID]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Conditional showing of the MicDrop button 
  It should be shown when 
  - We are in the CX
  - In the native app
  - In the IOS app
  */

  const [micDropAvailable, setMicDropAvailable] = useState(
    externalConnectionAvailable(),
  );

  const fromMultiRecorder = location.state?.from === 'multi-recorder';
  const breadcrumbItems = [
    fromMultiRecorder
      ? { name: 'Multi Recorder', path: '/User/conversations/multi-recorder' }
      : { name: 'Visits', path: '/User/conversations' },
    {
      name: (
        <span
          onClick={() => setShowEditorFields('patient')}
          style={{ cursor: 'pointer' }}
        >
          <i className="fa fa-user"></i>
          {` ${truncateText(group || 'Unknown')}`}
          <i className="fa fa-pen" style={{ marginLeft: '8px' }}></i>
        </span>
      ),
      path: '',
    },
    {
      name: (
        <span
          onClick={() => setShowEditorFields('visit')}
          style={{ cursor: 'pointer' }}
        >
          <i className="fa fa-file-text"></i>
          {` ${truncateText(name || 'Untitled')}`}
          <i className="fa fa-pen" style={{ marginLeft: '8px' }}></i>
        </span>
      ),
      path: '',
    },
  ];

  // Wrapper function to get existing values from webapp questions state
  const getExistingValuesFromWebapp = async () => {
    const existingValuesMapping = {};

    Object.keys(questions).forEach((questionKey) => {
      const answerValue = questions[questionKey];
      if (!answerValue) return;

      // Extract existing values for objects, use directly for primitives
      existingValuesMapping[questionKey] =
        typeof answerValue === 'object' && answerValue !== null
          ? extractExistingValuesOnly(answerValue)
          : answerValue;
    });

    return existingValuesMapping;
  };

  const handleGetComplianceScore = async () => {
    await handleGetComplianceScoreUtil({
      conversationID,
      complianceScoreLoading,
      setComplianceScoreLoading,
      apiClient,
      getExistingValuesMapping: getExistingValuesFromWebapp,
      _setComplianceScore,
      _setComplianceFeedback,
      _setComplianceCriteriaSource,
      _setStatus,
    });
  };

  const handleComprehendClick = async () => {
    // Note generation is one of the three clinician actions that evaluate
    // vendor subscriptions (docs/vendor-apps-contract.md §3).
    if (vendorAppsOn) vendorStore.requestEvaluate('note');
    await comprehend(undefined, null, (key, status) => {
      // This updates each section's refresh button via the callback
      document.dispatchEvent(
        new CustomEvent('updateComprehendStatus', {
          detail: { key, status },
        }),
      );
    });
  };

  const handleLearnTemplate = async () => {
    setBannerText('Learning (you can leave this page)');
    setAiThinking(true);
    logGAEvent('learn_clicked', apiClient, {
      configID: configId,
      conversationID: conversationID,
    });

    try {
      const sectionKeys = Object.keys(questions || {});
      if (sectionKeys.length === 0) {
        setAiThinking(false);
        return;
      }

      // Native Learn has the edited note here; it has no EMR page to read.
      saveLearnExample(apiClient, conversationID, configId, questions, {
        URL: 'https://app.comprehendpt.com',
      });

      const results = await learnTemplateSections(
        apiClient,
        conversationID,
        configId,
        sectionKeys.map((key) => ({ key, usersEdits: questions[key] })),
      );
      const learnedChanges = results.some(
        (result) => result && (result.newInstruction || result.newExamples),
      );
      const newTemplateQuestions = { ...templateQuestions };
      const newTemplateSamples = { ...templateSamples };

      results.forEach((result) => {
        if (!result) return;
        if (result.justification) {
          console.log(
            `--------------${result.key}-----------------\n` +
              `Justification: ${result.justification}\n` +
              `-----------------------------------------\n` +
              `Learn Template Result:`,
            result,
            `-----------------------------------------\n`,
          );
        }

        if (result.newInstruction) {
          newTemplateQuestions[result.key] = result.newInstruction;
        }

        if (result.newExamples) {
          newTemplateSamples[result.key] = result.newExamples;
        }
      });

      _setTemplateQuestions(newTemplateQuestions);
      _setTemplateSamples(newTemplateSamples);
      // A failed section names itself in the banner, so leave it up longer.
      setBannerText(learnResultBanner(results, learnedChanges));
      setTimeout(
        () => setAiThinking(false),
        results.some((result) => result.failed) ? 6000 : 2000,
      );
    } catch (error) {
      console.error('Learn template error:', error);
      _setStatus('Learn template failed.');
      setAiThinking(false);
    }
  };

  // Update actionsRef with current handlers and state for external access (tools, etc)
  actionsRef.current = {
    comprehend,
    setTranscript,
    setConfig,
    transcript,
  };

  return (
    <>
      <style>{`
        @keyframes compliance-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        /* --- Capture block: Live Visit / Dictation split buttons + Comprehend --- */
        .capture-actions .capture-row { display: flex; align-items: stretch; gap: 6px; }
        .capture-actions .capture-row > * { flex: 1 1 0; min-width: 0; }
        /* Chevrons are a fixed sliver; the wide half of each split button takes the rest. */
        .capture-actions .cx-chevron-button,
        .capture-actions .mic-split-group > button + button:last-of-type {
          width: 1.5rem !important; min-width: 1.5rem !important; flex: 0 0 1.5rem !important;
          padding-left: 0 !important; padding-right: 0 !important;
          display: inline-flex; align-items: center; justify-content: center;
        }
        /* Scoped to .capture-cell on purpose: DictationButton's own .mic-split-group
           is a row child and must keep the flex: 1 1 0 above, or it takes the row. */
        .capture-actions .capture-cell .mic-split-group { flex: 1 1 auto; min-width: 0; }
        .capture-actions .mic-split-group > button:first-of-type { flex: 1 1 auto; min-width: 0; padding-right: 10px; }
        .capture-actions .mic-label-stack { width: auto !important; flex: 1 1 auto; }
        /* Comprehend full width; stays a positioned ancestor for its menu. */
        .capture-actions .capture-comprehend { position: relative; display: block; width: 100%; }
        .capture-actions .capture-comprehend > .dropdown-container,
        .capture-actions .capture-comprehend > button { width: 100%; }
        /* Narrow surfaces: reclaim padding + icon + label so two-word labels fit
           instead of being sliced at the chevron. !important beats inline styles. */
        .capture-compact .mic-split-group > button:first-of-type { padding-left: 6px; padding-right: 6px; gap: 4px; }
        .capture-compact .mic-split-group > button:first-of-type > .fa { font-size: 1.4em !important; margin-right: 3px !important; }
        .capture-compact .mic-label-stack > span:first-child {
          font-size: 13px !important; max-width: 100%; overflow: hidden; text-overflow: ellipsis;
        }
      `}</style>
      <LoadingBanner showBanner={aiThinking} loadingText={bannerText} />
      {showInstructionAssistant && (
        <InstructionAssistantModal
          isOpen={instructionAssistantOpen}
          onClose={() => setInstructionAssistantOpen(false)}
          apiClient={apiClient}
          configId={configId}
          getSectionMetadata={getInstructionAssistantSectionMetadata}
          onSaveToTemplate={instructionAssistantUpdate.applyTemplate}
          onSaveToTemplateAndNote={instructionAssistantUpdate.applyTemplateAndNote}
          visitTranscript={transcript}
        />
      )}
      <Header isLite={true} flush />
      {/* New Patient Modal */}
      <PatientModal
        isOpen={isPatientModalOpen}
        onClose={() => setPatientModelOpen(false)}
        onSave={newPatient}
      ></PatientModal>
      {/* Page content */}
      {/* Edge to edge below 768px (cpt-visit-flush), like the extension's visit
          view; wide screens keep the usual layout. */}
      <Container className="mt--7 cpt-visit-flush" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <NavigationPath paths={breadcrumbItems} />
              <CardHeader className="border-0" style={{ padding: '0.4rem' }}>
                <div style={{ width: '100%', textAlign: 'center' }}>
                  {isOnline ? (
                    ''
                  ) : (
                    <span
                      style={{
                        color: 'blue',
                        fontSize: '0.95rem',
                        textAlign: 'center',
                      }}
                    >
                      Unstable Internet connection was detected, might affect
                      your transcript!
                    </span>
                  )}
                </div>
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <Label id="status" style={{ fontSize: '0.95rem' }}>
                    {status}
                  </Label>
                </div>
                <div
                  /* Compact sizing once the row is tight enough to stack —
                     halves get narrow there too, and "Resume Visit" would clip
                     at the chevron exactly as it did in the CX panel. */
                  className={
                    stackComprehend
                      ? 'capture-actions capture-compact'
                      : 'capture-actions'
                  }
                  style={{
                    display: 'flex',
                    flexDirection: stackComprehend ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: stackComprehend ? 'stretch' : 'center',
                    gap: stackComprehend ? '6px' : '0',
                  }}
                >
                  <div
                    className={stackComprehend ? 'capture-row' : undefined}
                    style={
                      stackComprehend
                        ? undefined
                        : { display: 'flex', alignItems: 'center', gap: '6px' }
                    }
                  >
                    <div className={stackComprehend ? 'capture-cell' : undefined}>
                      <AudioRecorder
                        recordButtonId="audio-recorder-button"
                        conversationID={conversationID}
                        refresh={updateConversationDetails}
                        setStatus={_setStatus}
                        seconds_transcribed={secondsTranscribed} // Pass the value of seconds_transcribed
                        // Visit wording only once dictation is enabled; without it
                        // the button keeps the Record/Resume defaults.
                        startLabel={showDictation ? 'Live Visit' : undefined}
                        resumeLabel={showDictation ? 'Resume Visit' : undefined}
                      />
                    </div>
                    {/* Dictation only makes sense once Clinical Facts is on
                        screen — that's where the transcribed text lands. */}
                    {showDictationButton && (
                      <DictationButton
                        apiClient={apiClient}
                        onText={appendVisitNotes}
                      />
                    )}
                  </div>
                  <Button
                    id="comprehend-button"
                    className={stackComprehend ? 'capture-comprehend' : undefined}
                    color="primary"
                    size="sm"
                    onClick={handleComprehendClick}
                    disabled={disableComprehend}
                    style={{
                      padding: '5px',
                      opacity: disableComprehend ? 0.6 : 1,
                      cursor: disableComprehend ? 'not-allowed' : 'pointer',
                      ...(stackComprehend
                        ? {
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }
                        : {}),
                    }}
                  >
                    <img
                      alt="Comprehend"
                      src={require('../assets/img/brand/mascot_light.png')}
                      style={{
                        marginRight: '10px',
                        height: '2.3em',
                        fontSize: '1rem',
                      }}
                    />
                    Comprehend
                  </Button>
                </div>
              </CardHeader>
              <Tabs
                id="conversation-tabs"
                activeKey={activeTab}
                onSelect={(key) => setActiveTab(key)}
                className="mb-3 custom-tabs"
              >
                {renderWorkspaceTabs(workspace, [
                /* Response Tab */
                <Tab
                  eventKey="response"
                  title={
                    <>
                      <WorkspaceIcon id="note" size={17} style={{ marginRight: '9px' }} />
                      Note
                    </>
                  }
                >
                  <div className="list-container">
                    {/* <ul> */}
                    <ul
                      style={{
                        paddingInlineStart: '5px',
                        paddingInlineEnd: '5px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                        }}
                      >
                        <div
                          id="outline-selector-template"
                          style={{
                            flexGrow: 1,
                            minWidth: '160px',
                            marginLeft: '0px',
                          }} // Ensure it grows and doesn't overflow
                        >
                          <OutlinedSelector
                            label="Template"
                            options={uniqueConfigs}
                            value={value}
                            onChange={setConfig}
                            flag={true}
                          />
                        </div>
                        {!disableEditing && (
                          <Button
                            color="primary"
                            size="med"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: 'fit-content',
                              width: '85px',
                              marginLeft: 0,
                              marginRight: 0,
                            }}
                            onClick={handleLearnTemplate}
                            id="learn-button"
                          >
                            <i
                              className="fa fa-brain"
                              style={{
                                marginRight: '4px',
                                fontSize: '1rem',
                              }}
                            ></i>
                            Learn
                          </Button>
                        )}
                        {showInstructionAssistant && (
                          <Button
                            color="primary"
                            size="med"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '43px',
                              width: '48px',
                              marginLeft: 0,
                              marginRight: 0,
                            }}
                            onClick={() => setInstructionAssistantOpen(true)}
                            id="instruction-assistant-button"
                            title="Instruction Assistant"
                            aria-label="Instruction Assistant"
                          >
                            <i
                              className="fa fa-wand-magic-sparkles"
                              style={{ fontSize: '1rem' }}
                            ></i>
                          </Button>
                        )}
                        <div
                          style={{
                            flexGrow: 0, // Do not allow it to grow
                            flexShrink: 1, // Allow shrinking to prevent overflow
                            minWidth: '50px', // Minimum width to prevent collapsing
                            maxWidth: '100px', // Prevent it from overflowing
                          }}
                        >
                          <OptionsDropdown
                            disableEditing={disableEditing}
                            apiClient={apiClient}
                            configId={configId}
                            userEmail={userEmail}
                            conversationID={conversationID}
                            configEMR={emr}
                          />
                        </div>
                      </div>
                      {(instructionAssistantUpdate.state.status !== 'idle' ||
                        instructionAssistantUpdate.state .showInstructionModal) && (
                        <div style={{ marginTop: '10px', maxWidth: '420px' }}>
                          <InstructionUpdateStatusStrip
                            status={instructionAssistantUpdate.state.status}
                            kind={instructionAssistantUpdate.state.kind}
                            onUndo={instructionAssistantUpdate.openUndo}
                          />
                          <InstructionUpdateUndoModal
                            isOpen={instructionAssistantUpdate.state.showInstructionModal}
                            kind={instructionAssistantUpdate.state.kind}
                            oldInstructions={instructionAssistantUpdate.state.oldInstructions}
                            newInstructions={instructionAssistantUpdate.state.newInstructions}
                            oldExamples={instructionAssistantUpdate.state.oldExamples}
                            newExamples={instructionAssistantUpdate.state.newExamples}
                            onResolveInstructions={instructionAssistantUpdate.resolveUndo}
                          />
                        </div>
                      )}
                      <Collapsible
                        id="transcript-section-collapsible"
                        label="Transcript"
                        content={transcript}
                        setContent={setTranscript}
                        copyPaste={true}
                        setStatus={_setStatus}
                        conversationID={conversationID}
                        setRefresh={setRefresh}
                        onWriteNotesPencilClick={toggleWriteNotes}
                      />
                      {showWriteNotes && (
                        <WriteNotesCollapsible
                          content={visitNotes}
                          setContent={setVisitNotes}
                        />
                      )}
                      {/* Vendor Apps: what connected apps told us about this patient — its own field,
                          shown whenever there is any, independent of the Clinical Facts toggle. Read-only: the app's latest data wins. */}
                      {isVendorAppsEnabled(apiClient) && <AppData appContext={appContext} />}
                      {micDropAvailable && <MicDrop questions={questions} />}
                      <div id="all-sections">
                        {Object.keys(questions).map((key, index) => {
                          return (
                            <Collapsible // User can edit instructions, add/hide samples and generate response
                              id={`section-collapsible-${index}`}
                              key={key}
                              label={key}
                              content={questions[key]}
                              setContent={(newAnswer) =>
                                updateQuestion(key, newAnswer)
                              }
                              comprehend={comprehend}
                              copyPaste={true}
                              onAddSample={() =>
                                onAddSample(
                                  key,
                                  configId,
                                  _setTemplateSamples,
                                  apiClient,
                                )
                              }
                              onEditSample={(index, newText) =>
                                onEditSample(
                                  key,
                                  index,
                                  newText,
                                  configId,
                                  _setTemplateSamples,
                                  apiClient,
                                )
                              }
                              questionKey={key}
                              samples={templateSamples}
                              openConfirmationModal={openConfirmationModal}
                              instruction={templateQuestions[key]}
                              setInstruction={(newInstructions) =>
                                updateInstructions(key, newInstructions)
                              }
                              configID={configId}
                              sectionText={key}
                              disabled={disableEditing}
                              triggerShow={triggerShow}
                            />
                          );
                        })}
                      </div>
                      {/* Vendor Apps: what each connected app is asking of this visit, as a subsection under the template's own sections. */}
                      {isVendorAppsEnabled(apiClient) && <VendorSubscriptionsSection appContext={appContext} />}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          width: '100%',
                        }}
                      >
                        <div style={{ display: 'flex', gap: '10px' }}>
                          {/* Button for Copy All */}
                          <Button
                            color="primary"
                            size="med"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            onClick={copyAll}
                          >
                            <span
                              className="fa fa-clipboard fa-2x"
                              style={{ marginRight: '5px' }}
                            />
                            Copy All Details
                          </Button>
                          {/* Button for Copy Response */}
                          <Button
                            color="primary"
                            size="med"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            onClick={copyResponse}
                          >
                            <span
                              className="fa fa-clipboard fa-2x"
                              style={{ marginRight: '5px' }}
                            />
                            Copy Responses Only
                          </Button>
                        </div>
                      </div>
                    </ul>
                  </div>
                </Tab>,

                /* Chat Tab */
                <Tab
                  eventKey="chat"
                  title={
                    <>
                      <WorkspaceIcon id="chat" size={17} style={{ marginRight: '9px' }} />
                      Ask
                    </>
                  }
                >
                  {conversationID ? (
                    <Chat
                      conversationId={conversationID}
                      apiClient={apiClient}
                      groupId={groupId}
                      isActive={workspace.isShown('chat')}
                    />
                  ) : (
                    <p>Loading chat...</p>
                  )}
                </Tab>,

                /* Compliance Tab */
                apiClient.showComplianceScore && (
                  <Tab
                    eventKey="compliance"
                    title={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {complianceScoreLoading ? (
                          <i
                            className="fa fa-spinner fa-spin"
                            style={{
                              marginRight: '5px',
                              fontSize: '1.2rem',
                            }}
                          />
                        ) : complianceScore ? (
                          <>
                            {/* The shared scale: components/Misc/complianceGrades.js */}
                            <span
                              style={{
                                color: GRADE_TEXT_COLORS[gradeForScore(complianceScore)],
                                marginRight: '5px',
                                fontWeight: 'bold',
                                fontSize: '1.2rem',
                              }}
                            >
                              {gradeForScore(complianceScore)}
                            </span>
                          </>
                        ) : (
                          <WorkspaceIcon id="compliance" size={17} style={{ marginRight: '6px' }} />
                        )}
                        <span style={{ fontSize: '1rem', fontWeight: 500 }}>
                          {scoreSurfaceLabel}
                        </span>
                      </div>
                    }
                  >
                    {apiClient.showComplianceScore && (
                      <div
                        id="compliance-score"
                        style={{
                          flex: 'none',
                          alignSelf: 'center', // Center alignment on smaller screens
                          width: '100%',
                        }}
                      >
                        {/* Get Compliance Score Button */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            padding: '20px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                            }}
                          >
                            <Button
                              color="primary"
                              onClick={handleGetComplianceScore}
                              disabled={complianceScoreLoading}
                              style={{
                                borderRadius: '20px',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '10px 16px',
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                              {complianceScoreLoading ? (
                                <>
                                  <i
                                    className="fa fa-spinner fa-spin"
                                    style={{ fontSize: '1.1rem' }}
                                  ></i>
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fa fa-refresh"
                                    style={{
                                      fontSize: '1.1rem',
                                      marginRight: '6px',
                                    }}
                                  ></i>
                                  {scoreRefreshLabel}
                                </>
                              )}
                            </Button>
                            <i
                              id="compliance-score-info-icon"
                              className="fa fa-info-circle"
                              style={{
                                fontSize: '0.9rem',
                                cursor: 'help',
                                color: '#5e72e4',
                              }}
                            ></i>
                          </div>
                          <Tooltip
                            placement="top"
                            target="compliance-score-info-icon"
                            id="compliance-score-info-tooltip"
                          >
                            Pulls your updated note and re-calculates your{' '}
                            {scoreSurfaceLabel.toLowerCase()} score.
                          </Tooltip>
                        </div>
                        <ComplianceScore
                          score={complianceScore}
                          height="120px"
                          feedback={complianceFeedback}
                          criteriaSource={complianceCriteriaSource}
                        />
                      </div>
                    )}
                  </Tab>
                ),
                /* Coding Tab */
                apiClient.features?.show_coding_tab && (
                  <Tab
                    eventKey="coding"
                    title={
                      <>
                        <WorkspaceIcon id="coding" size={17} style={{ marginRight: '9px' }} />
                        Coding
                      </>
                    }
                  >
                    <Coding
                      key={conversationID}
                      conversationID={conversationID}
                      apiClient={apiClient}
                      initialResults={codingRecommendations}
                      initialPayerName={codingPayerName}
                      noteType={config}
                      autoRun={codingAutoRun}
                      getCurrentNote={getExistingValuesFromWebapp}
                    />
                  </Tab>
                ),
                ], activeTab, { compliance: complianceCompactTitle(complianceScore, complianceScoreLoading) })}
              </Tabs>
            </Card>
          </div>
        </Row>
      </Container>
      <Modal
        isOpen={!!showEditorFields}
        onClose={() => setShowEditorFields(false)}
        isCloseButton={false}
        onEnterClose={true}
        hideCross={true}
      >
        <h4>
          {showEditorFields === 'visit'
            ? 'Edit Visit Name'
            : 'Edit Patient Name'}
        </h4>
        <div
          id="outline-selector-patient"
          style={{
            marginTop: '20px',
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: '45%',
            minWidth: '100px',
          }}
        >
          {showEditorFields === 'visit' && (
            <EditableLabel
              labelText="Visit Name"
              text={name}
              onChange={setName}
              className="h4"
              inputRef={inputRef}
            />
          )}
          {showEditorFields === 'patient' && (
            <OutlinedSelector
              label="Patient"
              options={uniqueGroups}
              value={group}
              onChange={(val) => {
                setGroup(val);
                setShowEditorFields(false);
              }}
            />
          )}
        </div>
      </Modal>
      <ConfirmationModal
        isOpen={confirmationDetails.isOpen}
        onClose={() =>
          setConfirmationDetails({ ...confirmationDetails, isOpen: false })
        }
        onConfirm={handleConfirmAction}
      >
        {confirmationDetails.type === 'sample'
          ? 'Are you sure you want to delete this sample? It will be permanently removed from the template.'
          : 'Are you sure you want to delete this template?'}
      </ConfirmationModal>
      <ToastContainer />
      <ConfirmationModal
        isOpen={popupVisible}
        onClose={() => setPopupVisible(false)}
        onConfirm={handleRateLimitConfirmation}
        confirmButtonText="Proceed with Downgraded Service"
      >
        Rate limit exceeded! Please wait {rateLimit} minutes before trying to
        generate a response or use a reduced model. The reduced model may impact
        the quality and accuracy of the generated notes.
      </ConfirmationModal>

    </>
  );
};

export default ConversationDetails;
